import {
  Button,
  Layout,
  Modal,
  notification,
  Skeleton,
  Space,
  Typography,
  theme,
} from "antd";
import React, { useRef } from "react";
import {
  deleteUser,
  initInstitutionUserSlice,
  setPropertiesUser,
  updateUser,
} from "../Slices/InstitutionUser.slice";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomForm from "../../../shared/components/CustomForm";
import routes from "../../../engine/routes";
import { renderUserName, roles, subRoles } from "../../../utils/constants";
import { InstitutionsUserForms } from "./InstitutionsUserForms";
import { ActionDropdown } from "../../../shared/components/ActionDropdown/ActionDropdown";
import { ActionRoleSpecific } from "../../../engine/AuthRoute";
import { http } from "../../../engine/api/http";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  doesThisEmailExist,
  sendEmailInvitation,
} from "../../../engine/api/apiRequest";
import { isSuperAdmin } from "../../../engine/hooks/hooks.helpers";
import { RiDeleteBinLine, RiMailSendLine } from "react-icons/ri";
import {
  confirmDeleteUserInstitution,
  confirmFinalUserDelete,
} from "../../../shared/components/Modals/modals";
import { useTranslation } from "react-i18next";
import { CiMail } from "react-icons/ci";

const { Content, Sider, Header, Footer } = Layout;

export function InstitutionUser({ unitId, expanded, onClose, isNew = false }) {
  const { id, user: userId } = useParams();
  const { t } = useTranslation(); // Initialize useTranslation

  const { institution, loading, user } = useSelector(state => state.user);
  const all = useSelector(state => state.user);
  const { init, clear } = initInstitutionUserSlice({
    userId,
    institutionId: id,
  });

  const navigate = useNavigate();

  const { users } = useSelector(state => state.institution);

  const superAdmin = isSuperAdmin();

  const isFirst =
    (!loading && users.length === 0) ||
    (users.length === 1 && userId !== "new");

  const dispatch = useDispatch();

  const formRef = useRef(null);

  const {
    token: { colorPrimary },
  } = theme.useToken();

  const saveNewUser = async data => {
    let exist = userId === "new" ? await doesThisEmailExist(data.email) : false;

    const isIntervenant =
      data.subRoles.length === 1 &&
      data.subRoles.includes(subRoles.institutionIntervenant);
    if (exist) {
      notification.error({
        message: t("institutionUser.emailAlreadyExists"),
        duration: 5000,
      });
    } else {
      if (userId === "new") {
        data.institution = id;
        data.role = roles.institution;
      }

      await dispatch(setPropertiesUser(data));

      Modal.confirm({
        open: false,
        width: 500,
        title: t("institutionUser.whatDoYouWantToDo"),
        icon: <ExclamationCircleOutlined />,
        onOk: () => {
          dispatch(updateUser());
        },
        footer: [
          <Space
            style={{
              marginTop: 30,
              display: "flex",
              justifyContent: "end",
            }}
          >
            <Button
              key={"cancel"}
              size={"middle"}
              onClick={() => {
                Modal.destroyAll();
              }}
            >
              {t("institutionUser.cancel")}
            </Button>
            <Button
              key={"createonly"}
              style={{ background: colorPrimary }}
              size={"middle"}
              type={"primary"}
              onClick={() => {
                dispatch(updateUser({ invitation: false }));
                Modal.destroyAll();
              }}
            >
              {t("institutionUser.create")}
            </Button>
            <Button
              disabled={isIntervenant}
              style={{ background: colorPrimary }}
              size={"middle"}
              type={"primary"}
              onClick={() => {
                dispatch(updateUser({ invitation: true }));
                Modal.destroyAll();
              }}
            >
              {t("institutionUser.createAndSendInvitationEmail")}
            </Button>
          </Space>,
        ],
        content: t("institutionUser.createUserAndSendInvitationEmail"),
      });
    }
  };

  const onSubmitForm = async data => {
    let exist = false;

    if (data.email !== user.email) {
      exist = await doesThisEmailExist(data.email);
    }

    if (exist) {
      notification.error({
        message: t("institutionUser.emailAlreadyExists"),
        duration: 5000,
      });
    } else {
      await dispatch(setPropertiesUser(data));
      await dispatch(updateUser({ invitation: false }));
    }
  };

  const inputs = InstitutionsUserForms({ user, isFirst, userId });

  const emailInvitationDisabled = () => {
    if (user.email === "") {
      return true;
    }

    if (
      user?.subRoles?.length === 1 &&
      user?.subRoles?.includes(subRoles.institutionIntervenant)
    ) {
      return true;
    }
  };

  const deleteUserInstitution = async () => {
    const res = await http.get("/users/" + user._id + "/deleteStatus");

    confirmDeleteUserInstitution({
      onOk: async () => {
        confirmFinalUserDelete({
          onOk: async () => {
            await dispatch(deleteUser(user._id));
            notification.success({
              message: t("institutionUser.userDeleted"),
            });

            navigate(routes.institutions(id).detailUsers.path);
          },
          user: user,
        });
      },
      props: res.data,
    });
  };

  return (
    <>
      <Space
        style={{
          marginTop: "10px",
          flexDirection: "row",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Title level={3} className={""}>
          {userId === "new"
            ? routes.institutions(id).newUserInstitution().title
            : renderUserName(user)}
        </Typography.Title>
        <ActionDropdown>
          <ActionRoleSpecific only={[roles.admin, roles.permanance]}>
            <a
              onClick={() =>
                !emailInvitationDisabled() && sendEmailInvitation(userId)
              }
              disabled={emailInvitationDisabled()}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <RiMailSendLine size={18} />
              </span>
              {t("institutionUser.sendInvitationEmail")}
            </a>
          </ActionRoleSpecific>
          {isSuperAdmin() ? (
            <a onClick={deleteUserInstitution} key={"deleteUserInstitution"}>
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <RiDeleteBinLine size={18} color={"red"} />
              </span>
              {t("institutionUser.deleteUser")}
            </a>
          ) : null}
        </ActionDropdown>
      </Space>
      {loading ? (
        <Skeleton active />
      ) : (
        <>
          <CustomForm
            rule={routes.institutions(id).detailUser()}
            dataSource={inputs}
            handleSubmit={data =>
              userId === "new" ? saveNewUser(data) : onSubmitForm(data)
            }
            labelCol={24}
            refForm={formRef}
            wrapperCol={24}
            onValuesChange={undefined}
          />
        </>
      )}
    </>
  );
}
